import { useRender } from "@base-ui/react/use-render";
import { type Currency, formatAmount, type VolatilityWords } from "@loot-os/api-contract";
import heartIcon from "@loot-os/icons/icons/heart";
import { type ReactElement, type RefObject, useEffect, useRef } from "react";
import { cardStyles } from "../core/Card.styles.js";
import { IconButton } from "../core/IconButton.js";
import { useRiseCount } from "../core/useRiseCount.js";
import { cn } from "../foundations/cn.js";
import { STRETCHED_LINK } from "../foundations/interaction.js";
import { useLinkRender } from "../foundations/linkContext.js";
import { OptionalImage } from "../foundations/OptionalImage.js";
import { useThemeSpec } from "../foundations/themeContext.js";
import { Amount } from "./Amount.js";
import { BoxArt } from "./BoxArt.js";
import { useCurrency } from "./currencyContext.js";
import { ItemArt } from "./ItemArt.js";
import { VolatilityMeter } from "./VolatilityMeter.js";

/** A box's top item as its card shows it. */
export interface BoxCardTopCard {
  image: string;
  /** The item's name. */
  alt: string;
}

export interface BoxCardProps {
  name: string;
  /** The box's artwork. */
  image: string;
  /** Integer cents. */
  price: number;
  /** The most valuable item in it, in cents; shown as "Top pull". */
  topPull?: number;
  /**
   * The most valuable item's picture: it peeks out behind the art and slides over it on hover or
   * keyboard focus. `alt` is the item's name.
   */
  topCard?: BoxCardTopCard;
  /** A short tag in the well's corner: a game, a theme ("WATER"), NEW. */
  tag?: string;
  /**
   * How swingy the box is, on the volatility scale (0 mild to 16 wild), drawn as a meter between
   * the name and the price. Left out, or unrated, the card has no meter: a store that doesn't show
   * volatility passes nothing.
   */
  volatility?: number | null;
  /** The volatility meter's name and level words; the default copy's when left out. */
  volatilityWords?: VolatilityWords;
  /** One line under the name: who made it ("Built by Ava"). */
  byline?: string;
  /** The label before the top pull's value; tenant copy. */
  topPullLabel?: string;
  size?: "default" | "compact";
  /** Links the card to the box page (a stretched link on its name). Without it the card is static. */
  href?: string;
  /** The app's router link, e.g. `<Link to="/boxes/tide-pool" />`. */
  render?: ReactElement;
  /**
   * The shopper's heart for this box: saved or not (undefined while their favorites load, shown
   * unsaved), and the press that flips it.
   */
  favorite?: { saved: boolean | undefined; label: string; onToggle: () => void };
  /** Overrides the CurrencyProvider's currency. */
  currency?: Currency;
  className?: string;
}

/** How long a pointer rests on a card before it counts as hovering: a sweep across a row doesn't. */
const HOVER_INTENT_MS = 120;
/** A full sweep, rest to hover, lasts this many of the theme's slow durations. */
const PEEK_BEATS = 2.2;

/**
 * Where the box art and its top card sit at `p`, from rest (0) to hovered (1). The box steps
 * aside, turns a little toward upright and dims a touch; the card comes forward over it,
 * straightens and grows. Individual transform properties, so they compose with nothing else.
 */
function peekFrame(p: number) {
  const pct = (n: number) => `${n.toFixed(2)}%`;
  return {
    art: {
      translate: `${pct(-12 + 1.5 * p)} ${pct(0.5 - p)}`,
      rotate: `${(-6 + 3 * p).toFixed(2)}deg`,
      scale: "1.06",
      opacity: String(1 - 0.1 * p),
    },
    card: {
      translate: `${pct((-400 / 15) * p)} ${pct((-1300 / 135) * p)}`,
      rotate: `${(5 - 3.5 * p).toFixed(2)}deg`,
      scale: (1 + 0.1 * p).toFixed(3),
    },
  };
}

function cssMs(element: Element, name: string, fallback: number): number {
  const value = Number.parseFloat(getComputedStyle(element).getPropertyValue(name));
  return Number.isFinite(value) ? value : fallback;
}

/**
 * Hover intent on the card (`data-hovered`, after a short rest; keyboard focus counts at once),
 * and, with a top card, the peek: the art and the card play from wherever they are to rest or
 * to hovered through the Web Animations API on the theme's `--ease-smooth`, so leaving mid-way
 * turns back smoothly. Reduced motion keeps both where they rest; touch never hovers.
 */
function useBoxCardHover(
  surface: RefObject<HTMLDivElement | null>,
  art: RefObject<HTMLDivElement | null>,
  card: RefObject<HTMLSpanElement | null>,
  peek: boolean,
) {
  useEffect(() => {
    const root = surface.current;
    if (!root) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    let intent: ReturnType<typeof setTimeout> | undefined;
    let at = 0;
    let run: { animations: Animation[]; from: number; to: number } | null = null;

    const progress = () => {
      const timing = run?.animations[0]?.effect?.getComputedTiming();
      if (!run || typeof timing?.progress !== "number") return at;
      return run.from + (run.to - run.from) * timing.progress;
    };

    const peekTo = (target: number) => {
      const artEl = art.current;
      const cardEl = card.current;
      if (!peek || reduce || !artEl || !cardEl || typeof artEl.animate !== "function") return;
      if (run ? run.to === target : at === target) return;
      const from = progress();
      for (const animation of run?.animations ?? []) animation.cancel();
      run = null;
      at = from;
      const slow = cssMs(root, "--theme-duration-slow", 320);
      const duration = Math.max(1, Math.round(slow * PEEK_BEATS * Math.abs(target - from)));
      const easing = getComputedStyle(root).getPropertyValue("--ease-smooth").trim() || "ease-out";
      const a = peekFrame(from);
      const b = peekFrame(target);
      const options: KeyframeAnimationOptions = { duration, easing, fill: "forwards" };
      const animations = [
        artEl.animate([a.art, b.art], options),
        cardEl.animate([a.card, b.card], options),
      ];
      const current = { animations, from, to: target };
      run = current;
      animations[0]?.finished.then(
        () => {
          if (run !== current) return;
          at = target;
        },
        () => {},
      );
    };

    const hover = (on: boolean) => {
      if (on) root.setAttribute("data-hovered", "");
      else root.removeAttribute("data-hovered");
      peekTo(on ? 1 : 0);
    };
    const onEnter = (event: PointerEvent) => {
      if (event.pointerType === "touch") return;
      clearTimeout(intent);
      intent = setTimeout(() => hover(true), HOVER_INTENT_MS);
    };
    const onLeave = () => {
      clearTimeout(intent);
      if (!root.matches(":has(:focus-visible)")) hover(false);
    };
    const onFocus = () => {
      if (root.matches(":has(:focus-visible)")) hover(true);
    };
    const onBlur = (event: FocusEvent) => {
      if (!root.contains(event.relatedTarget as Node | null) && !root.matches(":hover"))
        hover(false);
    };
    root.addEventListener("pointerenter", onEnter);
    root.addEventListener("pointerleave", onLeave);
    root.addEventListener("focusin", onFocus);
    root.addEventListener("focusout", onBlur);
    return () => {
      clearTimeout(intent);
      root.removeEventListener("pointerenter", onEnter);
      root.removeEventListener("pointerleave", onLeave);
      root.removeEventListener("focusin", onFocus);
      root.removeEventListener("focusout", onBlur);
      for (const animation of run?.animations ?? []) animation.cancel();
      root.removeAttribute("data-hovered");
    };
  }, [surface, art, card, peek]);
}

/**
 * A box in a browse row or grid: its art floating in a dark well over a soft glow of its own
 * colors, then its name and the price as a bar. With `topCard` the box's most valuable item stands
 * in front of the art and comes forward over it on hover or focus. With `href` the card links to
 * the box page (a stretched link on its name); it never opens the box itself. Hovered (after a
 * short rest) the card lifts, its glow brightens and the price bar fills with the accent.
 */
export function BoxCard({
  name,
  image,
  price,
  topPull,
  topCard,
  tag,
  volatility,
  volatilityWords,
  byline,
  topPullLabel = "Top pull",
  size = "default",
  href,
  render,
  favorite,
  currency,
  className,
}: BoxCardProps) {
  const compact = size === "compact";
  const linked = Boolean(href || render);
  const fromContext = useCurrency();
  // The heart plays when the shopper saves the box, not when they take it back, or when their
  // favorites arrive.
  const saves = useRiseCount(favorite?.saved === undefined ? undefined : Number(favorite.saved));
  // Saved or not shows in the heart's shape as well as its color: an outline until saved, then
  // solid (the theme's style, or rounded where the theme draws outlines).
  const iconStyle = useThemeSpec().icons.style;
  const heartStyle = !favorite?.saved ? "outline" : iconStyle === "outline" ? "rounded" : iconStyle;
  const surfaceRef = useRef<HTMLDivElement>(null);
  const artRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLSpanElement>(null);
  useBoxCardHover(surfaceRef, artRef, cardRef, Boolean(topCard));
  // The link's name carries the price, so a reader moving by links hears what the box costs.
  const spokenPrice = formatAmount(price, currency ?? fromContext);
  const nameLink = useRender({
    render: useLinkRender(render),
    defaultTagName: "a",
    enabled: linked,
    props: {
      href,
      className: STRETCHED_LINK,
      children: (
        <>
          {name}
          <span className="sr-only">, {spokenPrice}</span>
        </>
      ),
    },
  });
  const rest = peekFrame(0);
  return (
    <div
      ref={surfaceRef}
      className={cn(
        cardStyles({ radius: "2xl", action: linked ? "link" : "none" }),
        "group relative flex flex-col gap-3 p-3",
        // Lifts and tints on hover intent, on the press spring so it settles rather than stops.
        linked &&
          "transition-[translate,scale,background-color] duration-base ease-smooth data-hovered:bg-bg-raised motion-safe:data-hovered:-translate-y-[3px]",
        className,
      )}
    >
      <div
        className={cn(
          "relative isolate overflow-hidden rounded-lg bg-bg-inset",
          compact ? "aspect-[6/5]" : "aspect-[216/260]",
        )}
      >
        {/* The glow behind the art takes the art's own colors; it rests dim and lifts on hover. */}
        <OptionalImage
          src={image}
          alt=""
          aria-hidden
          className="absolute top-[24%] left-[11%] -z-10 h-[58%] w-[78%] -translate-y-[10%] rounded-pill object-cover opacity-40 blur-[28px] saturate-150 transition-[opacity,translate] duration-slow ease-standard group-data-hovered:translate-y-0 group-data-hovered:opacity-90 motion-reduce:translate-y-0 motion-reduce:transition-none"
        />
        <div
          ref={artRef}
          className={cn(
            "absolute inset-x-0 flex justify-center",
            topCard ? "top-[9%] bottom-[9%]" : "top-[13%] bottom-[13%]",
            !topCard &&
              "transition-[translate] duration-base ease-standard motion-safe:group-data-hovered:-translate-y-1 motion-reduce:transition-none",
          )}
          style={topCard ? rest.art : undefined}
        >
          <BoxArt
            src={image}
            alt=""
            className="w-auto drop-shadow-[0_24px_28px_color-mix(in_oklab,var(--color-bg-canvas)_75%,transparent)]"
          />
        </div>
        {topCard && (
          // In front of the box at its lower right, upright but for a slight lean.
          <span
            ref={cardRef}
            className={cn(
              "pointer-events-none absolute top-[30%] h-[58%]",
              compact ? "right-[10%]" : "right-[7%]",
            )}
            style={rest.card}
          >
            <ItemArt
              src={topCard.image}
              alt={topCard.alt}
              className="h-full w-auto drop-shadow-[0_16px_20px_color-mix(in_oklab,var(--color-bg-canvas)_70%,transparent)]"
            />
          </span>
        )}
        {tag && (
          <span className="absolute top-2.5 left-2.5 rounded-pill bg-bg-surface/40 px-2 py-0.5 font-mono text-mono-meta text-text-secondary uppercase">
            {tag}
          </span>
        )}
      </div>
      <div className="flex flex-col gap-1 px-1">
        <span className="truncate font-body text-heading-h3 text-text-primary">
          {linked ? nameLink : name}
        </span>
        {byline && (
          <span className="truncate font-body text-body-b3 text-text-tertiary">{byline}</span>
        )}
        {topPull !== undefined && !compact && (
          <span className="inline-flex items-baseline gap-1 font-body text-body-b3 text-text-tertiary">
            {topPullLabel}
            <Amount
              cents={topPull}
              currency={currency}
              trimZeroCents
              className="font-body text-body-b3"
            />
          </span>
        )}
      </div>
      <VolatilityMeter
        value={volatility}
        label={volatilityWords?.label}
        levels={volatilityWords?.levels}
        className="px-1"
      />
      {/* The price as a bar that fills with the accent on hover intent. */}
      <span className="flex h-control-md items-center justify-center rounded-lg bg-bg-raised text-text-primary transition-colors duration-base ease-standard group-data-hovered:bg-accent-default group-data-hovered:text-text-on-accent motion-reduce:transition-none">
        <Amount cents={price} currency={currency} />
      </span>
      {favorite && (
        // A child of the card itself, after the name, so it paints above the stretched link and
        // takes its own presses. Inside the art's isolated stacking context it sat under the link.
        <IconButton
          icon={heartIcon}
          label={favorite.label}
          pressed={favorite.saved ?? false}
          onPressedChange={favorite.onToggle}
          playOn={saves}
          iconVariant={heartStyle}
          variant="ghost"
          size="sm"
          className="absolute top-5 right-5 z-10"
        />
      )}
    </div>
  );
}
